import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "../../ui/badge";
import { cn } from "../../../lib/utils";

export type SentimentType = "Positive" | "Neutral" | "Negative" | "Mixed";

interface SentimentIndicatorProps {
  sentiment: SentimentType;
  size?: "sm" | "md";
  showIcon?: boolean;
  animate?: boolean;
}

const sentimentConfig = {
  Positive: {
    variant: "success" as const,
    className: "bg-emerald-500 hover:bg-emerald-500/90",
    dotColor: "bg-emerald-500",
    icon: "+",
    emoji: "^",
    labelKey: "adminChatPage.positive",
    defaultLabel: "Positive",
  },
  Negative: {
    variant: "destructive" as const,
    className: "bg-red-500 hover:bg-red-500/90",
    dotColor: "bg-red-500",
    icon: "-",
    emoji: "!",
    labelKey: "adminChatPage.negative",
    defaultLabel: "Negative",
  },
  Mixed: {
    variant: "warning" as const,
    className: "bg-amber-500 hover:bg-amber-500/90",
    dotColor: "bg-amber-500",
    icon: "~",
    emoji: "~",
    labelKey: "adminChatPage.mixed",
    defaultLabel: "Mixed",
  },
  Neutral: {
    variant: "secondary" as const,
    className: "bg-slate-400 hover:bg-slate-400/90",
    dotColor: "bg-slate-400",
    icon: "=",
    emoji: "-",
    labelKey: "adminChatPage.neutral",
    defaultLabel: "Neutral",
  },
};

export const SentimentIndicator: React.FC<SentimentIndicatorProps> = ({
  sentiment,
  size = "md",
  showIcon = true,
  animate = false,
}) => {
  const { t } = useTranslation();
  const config = sentimentConfig[sentiment];
  const isSmall = size === "sm";
  const label = t(config.labelKey, config.defaultLabel);

  if (isSmall) {
    return (
      <div
        className={cn(
          "rounded-full",
          config.dotColor,
          "h-2.5 w-2.5",
          animate && "animate-pulse motion-reduce:animate-none"
        )}
        title={label}
        role="img"
        aria-label={label}
        data-testid={`sentiment-indicator-${sentiment.toLowerCase()}`}
      />
    );
  }

  return (
    <Badge
      variant={config.variant}
      data-testid={`sentiment-indicator-${sentiment.toLowerCase()}`}
      className={cn(
        config.className,
        "text-white font-medium flex items-center gap-1",
        animate &&
          "transition-transform duration-200 hover:scale-105 motion-reduce:transform-none"
      )}
    >
      {showIcon && (
        <span className="font-mono text-xs" aria-hidden="true">
          {config.icon}
        </span>
      )}
      <span>{label}</span>
    </Badge>
  );
};
